import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getSalesDrill, recalculateSalesDrill, getUnifiedSales } from '../../services/api';
import {
  TrendingUp, ChevronRight, RefreshCw, Calendar,
  DollarSign, Hash, Receipt, FileText,
  ChevronLeft, ChevronDown, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_LABELS = {
  pos: 'POS Local',
  comanda_pro: 'Comanda Pro',
  marketplace: 'Marketplace',
  menu_digital: 'Menu Digital',
  web: 'Web',
  manual: 'Venta Manual',
  local_pos: 'POS Local',
};

const SOURCE_COLORS = {
  pos: 'border-l-indigo-500',
  comanda_pro: 'border-l-blue-500',
  marketplace: 'border-l-amber-500',
  menu_digital: 'border-l-emerald-500',
  web: 'border-l-purple-500',
  manual: 'border-l-slate-500',
  local_pos: 'border-l-indigo-500',
};

const PAYMENT_LABELS = {
  cash: 'Efectivo', CASH: 'Efectivo',
  card: 'Tarjeta', CARD: 'Tarjeta',
  transfer: 'Transferencia', TRANSFER: 'Transferencia',
  cash_on_pickup: 'Efectivo al recoger',
  cash_on_delivery: 'Efectivo al recibir',
  other: 'Otro',
};

const PAYMENT_COLORS = {
  cash: 'bg-green-500', CASH: 'bg-green-500',
  card: 'bg-blue-500', CARD: 'bg-blue-500',
  transfer: 'bg-purple-500', TRANSFER: 'bg-purple-500',
  other: 'bg-slate-400',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-MX', {
    day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── Date preset helpers ──
const today = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const getPresetDates = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const fmt = (dt) => dt.toISOString().slice(0, 10);

  switch (preset) {
    case 'today':
      return { start: today(), end: today() };
    case 'week': {
      const dayOfWeek = now.getDay();
      const monday = new Date(y, m, d - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      return { start: fmt(monday), end: today() };
    }
    case 'this-month':
      return { start: fmt(new Date(y, m, 1)), end: today() };
    case 'last-month': {
      const first = new Date(y, m - 1, 1);
      const last = new Date(y, m, 0);
      return { start: fmt(first), end: fmt(last) };
    }
    case '3-months': {
      const first = new Date(y, m - 2, 1);
      return { start: fmt(first), end: today() };
    }
    case 'year':
      return { start: fmt(new Date(y, 0, 1)), end: today() };
    default:
      return null;
  }
};

const PRESETS = [
  { key: 'today', label: 'Hoy' },
  { key: 'week', label: 'Semana' },
  { key: 'this-month', label: 'Este mes' },
  { key: 'last-month', label: 'Anterior' },
  { key: '3-months', label: '3 meses' },
  { key: 'year', label: 'Año' },
  { key: 'custom', label: 'Personalizado' },
];

const LEVEL_TITLES = ['Por Origen', 'Por Sucursal', 'Por Caja', 'Detalle'];

// ── Components ──

const KPICard = ({ icon: Icon, label, value, sub, color = 'text-teal-600' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-1">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide">
      <Icon className="w-4 h-4" />
      {label}
    </div>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400">{sub}</div>}
  </div>
);

const DrillCard = ({ group, maxTotal, onClick }) => {
  const pct = maxTotal > 0 ? (group.total / maxTotal) * 100 : 0;
  const borderColor = SOURCE_COLORS[group.id] || 'border-l-slate-400';
  return (
    <button
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-200 border-l-4 ${borderColor} p-4 text-left hover:shadow-md hover:border-slate-300 transition-all group w-full`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-slate-800 text-sm truncate">{group.name}</span>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-teal-500 transition-colors flex-shrink-0" />
      </div>
      <div className="text-lg font-bold text-slate-900">{formatCurrency(group.total)}</div>
      <div className="text-xs text-slate-500">{group.count} tickets</div>
      <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-teal-500 rounded-full transition-all"
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </button>
  );
};

const Breadcrumb = ({ items, onNavigate }) => (
  <div className="flex items-center gap-1 text-sm flex-wrap">
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
        {i < items.length - 1 ? (
          <button
            onClick={() => onNavigate(i)}
            className="text-teal-600 hover:text-teal-800 hover:underline font-medium"
          >
            {item.label}
          </button>
        ) : (
          <span className="text-slate-700 font-semibold">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const PaymentBreakdown = ({ breakdown }) => {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;
  const maxVal = Math.max(...Object.values(breakdown).map((v) => v.total));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-700 mb-3 text-sm">Metodos de Pago</h3>
      <div className="space-y-2">
        {Object.entries(breakdown)
          .sort((a, b) => b[1].total - a[1].total)
          .map(([method, data]) => {
            const pct = maxVal > 0 ? (data.total / maxVal) * 100 : 0;
            const bg = PAYMENT_COLORS[method] || 'bg-slate-400';
            return (
              <div key={method}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">{PAYMENT_LABELS[method] || method}</span>
                  <span className="font-medium text-slate-800">{formatCurrency(data.total)}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${bg} rounded-full`} style={{ width: `${Math.max(pct, 2)}%` }} />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const TopProducts = ({ products }) => {
  if (!products || products.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="font-semibold text-slate-700 mb-3 text-sm">Top 10 Productos</h3>
      <div className="space-y-2">
        {products.map((p, i) => (
          <div key={p.product_id} className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
              <div className="text-xs text-slate-500">{p.quantity} unidades</div>
            </div>
            <span className="text-sm font-semibold text-slate-800 flex-shrink-0">{formatCurrency(p.revenue)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main page ──

const AnalisisVentas = () => {
  const { user } = useAuth();
  const businessId = user?.business_id;

  // Drill state
  const [drillPath, setDrillPath] = useState({});
  const [datePreset, setDatePreset] = useState('this-month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [drillData, setDrillData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Sales list for level 3
  const [salesList, setSalesList] = useState([]);
  const [salesTotal, setSalesTotal] = useState(0);
  const [salesPage, setSalesPage] = useState(0);
  const [salesLoading, setSalesLoading] = useState(false);
  const [expandedSale, setExpandedSale] = useState(null);
  const PAGE_SIZE = 20;

  // Initialize dates
  useEffect(() => {
    const dates = getPresetDates('this-month');
    if (dates) {
      setStartDate(dates.start);
      setEndDate(dates.end);
    }
  }, []);

  // Drill level computed
  const drillLevel = useMemo(() => {
    if (drillPath.source && drillPath.branch_id && drillPath.terminal_id) return 3;
    if (drillPath.source && drillPath.branch_id) return 2;
    if (drillPath.source) return 1;
    return 0;
  }, [drillPath]);

  // Breadcrumb items
  const breadcrumbItems = useMemo(() => {
    const items = [{ label: 'Todos', level: 0 }];
    if (drillPath.source) items.push({ label: drillPath.source_name || drillPath.source, level: 1 });
    if (drillPath.branch_id) items.push({ label: drillPath.branch_name || drillPath.branch_id, level: 2 });
    if (drillPath.terminal_id) items.push({ label: drillPath.terminal_name || drillPath.terminal_id, level: 3 });
    return items;
  }, [drillPath]);

  // Check if range includes closed months
  const closedMonths = useMemo(() => {
    if (!startDate || !endDate) return [];
    const now = new Date();
    const currentYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const months = [];
    const [sy, sm] = startDate.split('-').map(Number);
    const [ey, em] = endDate.split('-').map(Number);
    let y = sy, m = sm;
    while (y < ey || (y === ey && m <= em)) {
      const ym = `${y}-${String(m).padStart(2, '0')}`;
      if (ym < currentYM) months.push(ym);
      m++;
      if (m > 12) { m = 1; y++; }
    }
    return months;
  }, [startDate, endDate]);

  // Fetch drill data
  const loadDrillData = useCallback(async () => {
    if (!businessId || !startDate || !endDate) return;
    setLoading(true);
    try {
      const params = {
        business_id: businessId,
        start_date: startDate,
        end_date: endDate,
      };
      if (drillPath.source) params.source = drillPath.source;
      if (drillPath.branch_id) params.branch_id = drillPath.branch_id;
      if (drillPath.terminal_id) params.terminal_id = drillPath.terminal_id;

      const res = await getSalesDrill(params);
      setDrillData(res.data);
    } catch (err) {
      toast.error('Error al cargar datos de analisis');
    } finally {
      setLoading(false);
    }
  }, [businessId, startDate, endDate, drillPath]);

  // Fetch sales list for level 3
  const loadSalesList = useCallback(async () => {
    if (!businessId || drillLevel !== 3) {
      setSalesList([]);
      return;
    }
    setSalesLoading(true);
    try {
      const params = {
        business_id: businessId,
        start_date: startDate,
        end_date: endDate,
        source: drillPath.source,
        branch_id: drillPath.branch_id,
        terminal_id: drillPath.terminal_id,
        skip: salesPage * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      const res = await getUnifiedSales(params);
      if (Array.isArray(res.data)) {
        setSalesList(res.data);
        setSalesTotal(parseInt(res.headers?.['x-total-count'] || res.data.length, 10));
      } else if (res.data?.sales) {
        setSalesList(res.data.sales);
        setSalesTotal(res.data.total || res.data.sales.length);
      } else {
        setSalesList([]);
        setSalesTotal(0);
      }
    } catch {
      setSalesList([]);
    } finally {
      setSalesLoading(false);
    }
  }, [businessId, startDate, endDate, drillPath, drillLevel, salesPage]);

  useEffect(() => {
    if (startDate && endDate) loadDrillData();
  }, [loadDrillData]);

  useEffect(() => {
    if (drillLevel === 3) loadSalesList();
  }, [loadSalesList]);

  // Preset handler
  const handlePreset = (key) => {
    setDatePreset(key);
    if (key === 'custom') return;
    const dates = getPresetDates(key);
    if (dates) {
      setStartDate(dates.start);
      setEndDate(dates.end);
      setDrillPath({});
      setSalesPage(0);
    }
  };

  // Drill navigation
  const drillInto = (groupId, groupName) => {
    setSalesPage(0);
    setExpandedSale(null);
    if (drillLevel === 0) {
      setDrillPath({ source: groupId, source_name: groupName });
    } else if (drillLevel === 1) {
      setDrillPath((prev) => ({ ...prev, branch_id: groupId, branch_name: groupName }));
    } else if (drillLevel === 2) {
      setDrillPath((prev) => ({ ...prev, terminal_id: groupId, terminal_name: groupName }));
    }
  };

  const drillBack = (toLevel) => {
    setSalesPage(0);
    setExpandedSale(null);
    if (toLevel === 0) {
      setDrillPath({});
    } else if (toLevel === 1) {
      setDrillPath((prev) => ({ source: prev.source, source_name: prev.source_name }));
    } else if (toLevel === 2) {
      setDrillPath((prev) => ({
        source: prev.source, source_name: prev.source_name,
        branch_id: prev.branch_id, branch_name: prev.branch_name,
      }));
    }
  };

  // Recalculate cache
  const handleRecalculate = async () => {
    if (closedMonths.length === 0) return;
    setRecalculating(true);
    try {
      for (const ym of closedMonths) {
        await recalculateSalesDrill({ business_id: businessId, year_month: ym });
      }
      toast.success(`Cache recalculado para ${closedMonths.length} mes(es)`);
      loadDrillData();
    } catch {
      toast.error('Error al recalcular cache');
    } finally {
      setRecalculating(false);
    }
  };

  const summary = drillData?.summary;
  const groups = drillData?.groups;
  const topProducts = drillData?.top_products;
  const maxGroupTotal = groups ? Math.max(...groups.map((g) => g.total), 0) : 0;
  const totalPages = Math.ceil(salesTotal / PAGE_SIZE);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Analisis de Ventas</h1>
              <p className="text-sm text-slate-500">Vista drill-down de ventas</p>
            </div>
          </div>
          {closedMonths.length > 0 && (
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
                Recalcular
              </button>
              <span className="text-xs text-slate-400">Presiona si cancelaste o modificaste ventas de meses anteriores</span>
            </div>
          )}
        </div>

        {/* Date presets */}
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePreset(p.key)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                datePreset === p.key
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400'
              }`}
            >
              {p.label}
            </button>
          ))}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setDrillPath({}); setSalesPage(0); }}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
              />
              <span className="text-slate-400">-</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setDrillPath({}); setSalesPage(0); }}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
              />
            </div>
          )}
        </div>

        {/* Breadcrumb */}
        {drillLevel > 0 && (
          <Breadcrumb items={breadcrumbItems} onNavigate={drillBack} />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 text-teal-500 animate-spin" />
            <span className="ml-2 text-slate-500">Cargando...</span>
          </div>
        ) : drillData ? (
          <>
            {/* KPI Cards */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPICard icon={DollarSign} label="Total" value={formatCurrency(summary.total)} />
                <KPICard icon={Hash} label="Tickets" value={summary.count.toLocaleString()} color="text-blue-600" />
                <KPICard icon={Receipt} label="Promedio" value={formatCurrency(summary.avg_ticket)} color="text-purple-600" />
                <KPICard
                  icon={FileText}
                  label="Facturas"
                  value={`${summary.facturadas}/${summary.count}`}
                  color="text-amber-600"
                />
              </div>
            )}

            {/* Levels 0-2: Drill cards */}
            {drillLevel <= 2 && groups && groups.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  {LEVEL_TITLES[drillLevel]}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {groups.map((g) => (
                    <DrillCard
                      key={g.id}
                      group={g}
                      maxTotal={maxGroupTotal}
                      onClick={() => drillInto(g.id, g.name)}
                    />
                  ))}
                </div>
              </div>
            )}

            {drillLevel <= 2 && groups && groups.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                No hay ventas en este periodo
              </div>
            )}

            {/* Payment breakdown (all levels) */}
            {summary?.payment_breakdown && Object.keys(summary.payment_breakdown).length > 0 && (
              <div className={drillLevel === 3 ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : ''}>
                <PaymentBreakdown breakdown={summary.payment_breakdown} />
                {drillLevel === 3 && <TopProducts products={topProducts} />}
              </div>
            )}

            {/* Level 3 only: Top products if payment_breakdown is empty */}
            {drillLevel === 3 && (!summary?.payment_breakdown || Object.keys(summary.payment_breakdown).length === 0) && (
              <TopProducts products={topProducts} />
            )}

            {/* Level 3: Sales table */}
            {drillLevel === 3 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-700 text-sm">Ventas</h3>
                </div>
                {salesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 text-teal-500 animate-spin" />
                  </div>
                ) : salesList.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ticket</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Pago</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Factura</th>
                            <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {salesList.map((sale) => (
                            <React.Fragment key={sale.id}>
                              <tr className="hover:bg-slate-50">
                                <td className="px-4 py-2.5 font-medium text-slate-800">
                                  {sale.ticket_number || sale.id?.slice(0, 8)}
                                </td>
                                <td className="px-4 py-2.5 text-slate-600">{formatDate(sale.created_at)}</td>
                                <td className="px-4 py-2.5 text-right font-semibold text-slate-800">
                                  {formatCurrency(sale.total)}
                                </td>
                                <td className="px-4 py-2.5 text-slate-600">
                                  {PAYMENT_LABELS[sale.payment_method] || sale.payment_method || '-'}
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  {sale.facturado ? (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">CFDI</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  <button
                                    onClick={() => setExpandedSale(expandedSale === sale.id ? null : sale.id)}
                                    className="p-1 rounded hover:bg-slate-100"
                                  >
                                    {expandedSale === sale.id ? (
                                      <ChevronDown className="w-4 h-4 text-slate-400" />
                                    ) : (
                                      <Eye className="w-4 h-4 text-slate-400" />
                                    )}
                                  </button>
                                </td>
                              </tr>
                              {expandedSale === sale.id && sale.items && (
                                <tr>
                                  <td colSpan="6" className="px-4 py-3 bg-slate-50">
                                    <div className="space-y-1">
                                      {sale.items.map((item, idx) => (
                                        <div key={idx} className="flex justify-between text-xs text-slate-600">
                                          <span>{item.quantity}x {item.product_name || item.name}</span>
                                          <span>{formatCurrency(item.subtotal || item.quantity * item.price)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                        <button
                          onClick={() => setSalesPage((p) => Math.max(0, p - 1))}
                          disabled={salesPage === 0}
                          className="flex items-center gap-1 text-sm text-slate-600 hover:text-teal-600 disabled:opacity-40"
                        >
                          <ChevronLeft className="w-4 h-4" /> Anterior
                        </button>
                        <span className="text-sm text-slate-500">
                          Pag {salesPage + 1} de {totalPages}
                        </span>
                        <button
                          onClick={() => setSalesPage((p) => Math.min(totalPages - 1, p + 1))}
                          disabled={salesPage >= totalPages - 1}
                          className="flex items-center gap-1 text-sm text-slate-600 hover:text-teal-600 disabled:opacity-40"
                        >
                          Siguiente <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-slate-400 text-sm">No hay ventas en este periodo</div>
                )}
              </div>
            )}

            {/* Cached indicator */}
            {drillData?.cached && (
              <div className="text-xs text-slate-400 text-center">
                Datos en cache — ultimo calculo del mes cerrado
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 text-slate-400">
            Selecciona un rango de fechas para ver el analisis
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnalisisVentas;
